import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { clientErrors } from "@/lib/validators";
import { dateEs } from "@/lib/formatters";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, Edit, Trash2, Users as UsersIcon, AlertCircle } from "lucide-react";

const EMPTY = { name: "", rfc: "", email: "", phone: "", contact_name: "", address: "", city: "", state: "", zip: "", notes: "" };

export default function ClientsList() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients", { params: q ? { q } : {} });
      setRows(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setEditing({}); setForm(EMPTY); setErrors({}); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setErrors({}); };

  const save = async () => {
    const errs = clientErrors(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Revisa los campos marcados en rojo");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.name = form.name;
      if (editing?.id) {
        await api.patch(`/clients/${editing.id}`, payload);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/clients", payload);
        toast.success("Cliente creado");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar cliente "${c.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente eliminado");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="clients-list-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pe-label">MÓDULO 2</div>
          <h1 className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)]">Clientes</h1>
          <p className="text-slate-500 mt-1 text-sm">Validación real de RFC mexicano, email y teléfono.</p>
        </div>
        {can("clients_write") && (
          <Button onClick={openNew} className="pe-btn-primary text-white h-11" data-testid="btn-new-client">
            <Plus size={16} className="mr-1" /> Nuevo cliente
          </Button>
        )}
      </div>

      <div className="pe-card p-4 mt-6 flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-3.5 text-slate-400" />
          <Input placeholder="Buscar por nombre, RFC o email…" className="pl-9 h-11"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 data-testid="input-search-clients" />
        </div>
        <Button variant="outline" onClick={load} className="h-11" data-testid="btn-search-clients">Buscar</Button>
      </div>

      <div className="mt-4 pe-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[color:var(--pe-primary)] text-white text-left">
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">Nombre</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">RFC</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">Contacto</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">Teléfono / Email</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">Alta</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">Cargando…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="p-10 text-center">
                  <UsersIcon className="mx-auto text-slate-300" size={40} />
                  <div className="mt-3 font-bold text-slate-600">Sin clientes</div>
                  <div className="text-sm text-slate-500 mt-1">Crea el primer cliente para comenzar.</div>
                </td></tr>
              ) : (
                rows.map((c) => (
                  <tr key={c.id} className="border-t border-[color:var(--pe-border)] hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3 pe-numeric text-slate-700">{c.rfc || "—"}</td>
                    <td className="px-4 py-3">{c.contact_name || "—"}</td>
                    <td className="px-4 py-3">
                      <div>{c.phone || "—"}</div>
                      <div className="text-xs text-slate-500">{c.email || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{dateEs(c.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {can("clients_write") && (
                        <button onClick={() => openEdit(c)} className="p-1 text-slate-600 hover:text-[color:var(--pe-primary)]"
                                data-testid={`btn-edit-client-${c.id}`}>
                          <Edit size={16} />
                        </button>
                      )}
                      {can("clients_delete") && (
                        <button onClick={() => remove(c)} className="p-1 text-red-500 hover:text-red-700 ml-1"
                                data-testid={`btn-delete-client-${c.id}`}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2 mt-2">
            <FormField label="Nombre / Razón social *" error={errors.name} full>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-form-name" />
            </FormField>
            <FormField label="RFC" error={errors.rfc}>
              <Input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                     placeholder="XAXX010101000" data-testid="client-form-rfc" />
            </FormField>
            <FormField label="Contacto (persona)">
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                     data-testid="client-form-contact" />
            </FormField>
            <FormField label="Email" error={errors.email}>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="client-form-email" />
            </FormField>
            <FormField label="Teléfono" error={errors.phone}>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-form-phone" />
            </FormField>
            <FormField label="Dirección" full>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-form-address" />
            </FormField>
            <FormField label="Ciudad">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </FormField>
            <FormField label="Estado">
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </FormField>
            <FormField label="CP" error={errors.zip}>
              <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} maxLength={5} />
            </FormField>
            <FormField label="Notas" full>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </FormField>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="pe-btn-primary text-white" data-testid="btn-save-client">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, error, children, full }) {
  return (
    <div className={`space-y-1 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="pe-label">{label}</Label>
      {children}
      {error && (
        <div className="flex items-center gap-1 text-xs text-red-600">
          <AlertCircle size={12} /> {error}
        </div>
      )}
    </div>
  );
}
